import {createApiClient} from "./apiClient";
import {config} from "../config";

const apiClient = createApiClient({
    baseURL: config.bffUrl + '/api/',
});


export const fetchProfiles = async () => {
    try {
        // Prefer v2 which includes advanced filter groups
        const response = await apiClient.get('search-agents/v2');
        return response.data;
    } catch (err) {
        console.error('Error fetching profiles', err);
        throw err;
    }
}

export const fetchProfileNoticeSummary = async (selectedProfile) => {
    try {
        const response = await apiClient.get(`search-agents/${selectedProfile}/notices/summary`);
        return response.data;
    } catch (err) {
        console.error('Error fetching profile notice summary', err);
        throw err;
    }
}

// Aggregated summary for all profiles (backend should support this to avoid many round-trips)
export const fetchProfileNoticeSummaryAll = async () => {
    try {
        const response = await apiClient.get(`search-agents/summary`);
        return response.data;
    } catch (err) {
        console.error('Error fetching aggregated profile notice summary', err);
        throw err;
    }
}

export const fetchProfileNotices = async (selectedProfile, currentPage, pageSize, sortField, sortOrder, since, includeExpired = false, hideNotInterested = false) => {
    try {
        let url = `search-agents/${selectedProfile}/notices?page=${currentPage - 1}&size=${pageSize}&sortBy=${sortField}&order=${sortOrder}&includeExpired=${includeExpired}&hideNotInterested=${hideNotInterested}`;
        if (since) url += `&since=${encodeURIComponent(since)}`;
        const response = await apiClient.get(url);
        return response.data;
    } catch (err) {
        console.error('Error fetching profile notices', err);
        throw err;
    }
}

export const markProfileViewed = async (profileId) => {
    try {
        await apiClient.post(`search-agents/${profileId}/mark-viewed`);
    } catch (err) {
        console.error('Error marking profile as viewed', err);
        throw err;
    }
}

export const fetchProfileEndingContracts = async (selectedProfile, withinMonths, currentPage, pageSize) => {
    try {
        const response = await apiClient.get(
            `search-agents/${selectedProfile}/ending-contracts?withinMonths=${withinMonths}&page=${currentPage - 1}&size=${pageSize}`
        );
        return response.data;
    } catch (err) {
        console.error('Error fetching profile ending contracts', err);
        throw err;
    }
}

export const searchEndingContracts = async (payload) => {
    try {
        const response = await apiClient.post('contracts/ending', payload);
        return response.data;
    } catch (err) {
        console.error('Error searching ending contracts', err);
        throw err;
    }
}

export const updateProfile = async (payload) => {
    try {
        const response = await apiClient.post('create-search-agent', payload);
        return response.data;
    } catch (err) {
        console.error('Error updating profile', err);
        throw err;
    }
}

export const deleteProfile = async (id) => {
    try {
        const response = await apiClient.delete(`search-agents/${id}`);
        return response.data;
    } catch (err) {
        console.error('Error deleting profile', err);
        throw err;
    }
}

export const replaceProfile = async (id, payload) => {
    try {
        const response = await apiClient.post(`search-agents/v2/${id}/replace`, payload);
        return response.data;
    } catch (err) {
        console.error('Error replacing profile', err);
        throw err;
    }
}

export const updateProfileEmailNotifications = async (agentId, emailNotificationsEnabled) => {
    const response = await apiClient.patch(`search-agents/${agentId}/notifications`, { emailNotificationsEnabled });
    return response.data;
}

export const updateProfileAssignment = async (agentId, assignedHankoIds, assignedTeamId) => {
    const response = await apiClient.patch(`search-agents/${agentId}/assignment`, {
        assignedHankoIds: assignedHankoIds?.length > 0 ? assignedHankoIds : null,
        assignedTeamId: assignedTeamId || null,
    });
    return response.data;
}

/** Renames a profile; its search and matches stay as they are. */
export const renameProfile = async (agentId, name, previousName) => {
    const response = await apiClient.patch(`search-agents/${agentId}/name`, { name });
    // Search.jsx can hydrate legacy profiles from a payload cached under the profile's name.
    try {
        const oldKey = `searchProfilePayload:${previousName}`;
        const cached = previousName ? localStorage.getItem(oldKey) : null;
        if (cached) {
            localStorage.setItem(`searchProfilePayload:${name}`, cached);
            localStorage.removeItem(oldKey);
        }
    } catch (e) {
        // storage unavailable: nothing to move
    }
    return response.data;
}

/**
 * Adds CPV codes ([{ cpvCode, searchNested }]) to the profile's main filters. Existing matches are kept;
 * a 409 means the profile's main filters have no keywords or CPVs, where adding one would narrow it.
 */
export const addProfileCpvCodes = async (agentId, cpvCodes) => {
    const response = await apiClient.post(`search-agents/${agentId}/cpv-codes`, { cpvCodes });
    return response.data;
}
